import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../app/app.environment';

export interface ClientSetting {
  id: number;
  client_id: number;
  type: string;
  field: string;
  label: string;
  value: number;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientSettingsResponse {
  status: number;
  message: string;
  data: ClientSetting[];
}

export interface ModePermissions {
  wholesaleMode: boolean;
  quotationMode: boolean;
  suspenseMode: boolean;
  pricingGroupMode: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ClientSettingsService {
  private apiUrl = environment.apiURL + 'v1';
  private settingsSubject = new BehaviorSubject<ClientSetting[]>([]);
  private permissionsSubject = new BehaviorSubject<ModePermissions>({
    wholesaleMode: false,
    quotationMode: false,
    suspenseMode: false,
    pricingGroupMode: false,
  });

  public settings$ = this.settingsSubject.asObservable();
  public permissions$ = this.permissionsSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadClientSettings(): Observable<ClientSetting[]> {
    const clientId = this.getClientId();
    if (!clientId) {
      console.error('No client_id found in localStorage');
      return of([]);
    }

    return this.http
      .get<ClientSettingsResponse>(
        `${this.apiUrl}/order/getclientSettings/${clientId}`,
      )
      .pipe(
        // clientsettings.status is a varchar column, so the API sends "1"; normalize for the === 1 checks
        map((response) =>
          (response.data || []).map((s) => ({
            ...s,
            value: Number(s.value),
            status: Number(s.status),
          })),
        ),
        tap((settings) => {
          this.settingsSubject.next(settings);
          this.updatePermissions(settings);
        }),
        catchError((error) => {
          console.error('Error loading client settings:', error);
          return of([]);
        }),
      );
  }

  private getClientId(): string | null {
    const clientId = localStorage.getItem('client_id');
    if (clientId) {
      try {
        return JSON.parse(clientId);
      } catch {
        return clientId;
      }
    }
    return null;
  }

  private updatePermissions(settings: ClientSetting[]): void {
    const salesAndGeneralSettings = settings.filter(
      (setting) => setting.type === 'Sales' || setting.type === 'General',
    );

    const permissions: ModePermissions = {
      wholesaleMode: this.isFieldEnabled(
        salesAndGeneralSettings,
        'wholesaleprice',
      ),
      quotationMode: this.isFieldEnabled(
        salesAndGeneralSettings,
        'quotationmode',
      ),
      suspenseMode: this.isFieldEnabled(
        salesAndGeneralSettings,
        'suspensemode',
      ),
      pricingGroupMode: this.isFieldEnabled(
        salesAndGeneralSettings,
        'pricinggroupmode',
      ),
    };

    this.permissionsSubject.next(permissions);
  }

  private isFieldEnabled(settings: ClientSetting[], field: string): boolean {
    const setting = settings.find((s) => s.field === field);
    return setting ? setting.status === 1 && setting.value === 1 : false;
  }

  isWholesaleModeEnabled(): boolean {
    return this.permissionsSubject.value.wholesaleMode;
  }

  isQuotationModeEnabled(): boolean {
    return this.permissionsSubject.value.quotationMode;
  }

  isSuspenseModeEnabled(): boolean {
    return this.permissionsSubject.value.suspenseMode;
  }

  isPricingGroupModeEnabled(): boolean {
    return this.permissionsSubject.value.pricingGroupMode;
  }

  getCurrentPermissions(): ModePermissions {
    return this.permissionsSubject.value;
  }

  isCursorFocusEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const cursorFocusSetting = settings.find((s) => s.field === 'cursorfocus');
    return cursorFocusSetting
      ? cursorFocusSetting.status === 1 && cursorFocusSetting.value === 1
      : false;
  }

  /**
   * Check if barcode field is enabled
   */
  isBarcodeEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const barcodeSetting = settings.find((s) => s.field === 'barcode');
    // If setting exists with status=1, check value. Otherwise default to true (show barcode)
    return barcodeSetting
      ? barcodeSetting.status === 1 && barcodeSetting.value === 1
      : false;
  }

  isFreeQtyEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const freeQtySetting = settings.find(
      (s) => s.field === 'enable_free_qty' && s.type === 'Sales',
    );
    return freeQtySetting
      ? freeQtySetting.status === 1 && freeQtySetting.value === 1
      : false;
  }

  isContactEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const contactSetting = settings.find((s) => s.field === 'enable_contact');
    return contactSetting
      ? contactSetting.status === 1 && contactSetting.value === 1
      : false;
  }

  /**
   * Check if batch field is enabled
   */
  isBatchEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const batchSetting = settings.find((s) => s.field === 'batch');
    return batchSetting
      ? batchSetting.status === 1 && batchSetting.value === 1
      : false;
  }

  /**
   * Check if customer mobile number is mandatory
   * Only mandatory when both value = 1 AND status = 1
   */
  isCustomerMobileMandatory(): boolean {
    const settings = this.settingsSubject.value;
    const mobileSetting = settings.find((s) => s.field === 'customermobile');
    return mobileSetting
      ? mobileSetting.status === 1 && mobileSetting.value === 1
      : false;
  }

  /**
   * Check if discount after tax is enabled
   * Only mandatory when both value = 1 AND status = 1
   */
  isDiscountAfterTaxEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const discountSetting = settings.find((s) => s.field === 'discaftertax');
    return discountSetting
      ? discountSetting.status === 1 && discountSetting.value === 1
      : false;
  }

  /**
   * Get PDF template name from settings
   * Looks for 'invoicetemplate' or 'pdftemplate' field
   * Returns 'default' if not found or not enabled
   */
  getInvoiceTemplate(): string {
    const settings = this.settingsSubject.value;

    // Try different possible field names
    const templateSetting = settings.find(
      (s) =>
        s.field === 'invoicetemplate' ||
        s.field === 'pdftemplate' ||
        s.field === 'template',
    );

    // If setting exists and is enabled (status = 1), return the value as template name
    // The value field might contain a reference to template name mapping
    // For now, we'll use a simple mapping based on value
    if (templateSetting && templateSetting.status === 1) {
      // Map value to template name
      // This mapping should match what the backend sends
      switch (templateSetting.value) {
        case 1:
          return 'new-jaffer'; // A3 template
        case 2:
          return 'rathna-shree'; // A5 template
        case 3:
          return 'ptr-enterprise'; // A4 template
        default:
          return 'default'; // Thermal template
      }
    }

    return 'default'; // Fallback to thermal template
  }

  /**
   * Get all current settings
   */
  getSettings(): ClientSetting[] {
    return this.settingsSubject.value;
  }

  isLoyaltyPointsEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const loyaltySetting = settings.find((s) => s.field === 'rewardpoint');
    return !!(
      loyaltySetting &&
      loyaltySetting.status === 1 &&
      loyaltySetting.value === 1
    );
  }

  isOverAllDiscountEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const overallDiscountSetting = settings.find(
      (s) => s.field === 'overalldiscount',
    );
    return !!(
      overallDiscountSetting &&
      overallDiscountSetting.status === 1 &&
      overallDiscountSetting.value === 1
    );
  }

  isShippingDetailsEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const shippingDetailsSetting = settings.find(
      (s) => s.field === 'shippingdetails',
    );
    return !!(
      shippingDetailsSetting &&
      shippingDetailsSetting.status === 1 &&
      shippingDetailsSetting.value === 1
    );
  }

  isDecimalQuantityEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const decimalSetting = settings.find((s) => s.field === 'decimalhandling');
    return !!(
      decimalSetting &&
      decimalSetting.status === 1 &&
      decimalSetting.value === 1
    );
  }

  isDateEditModeEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const dateEditSetting = settings.find((s) => s.field === 'salesdate');
    return !!(
      dateEditSetting &&
      dateEditSetting.status === 1 &&
      dateEditSetting.value === 1
    );
  }

  /**
   * Check if counter session management is enabled
   * Counter session monitoring will only be active when this setting is enabled
   */
  isCounterSessionEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const counterSessionSetting = settings.find(
      (s) => s.field === 'countersession',
    );
    return !!(
      counterSessionSetting &&
      counterSessionSetting.status === 1 &&
      counterSessionSetting.value === 1
    );
  }

  /**
   * Get a specific setting by field name
   */
  getSetting(field: string): ClientSetting | undefined {
    return this.settingsSubject.value.find((s) => s.field === field);
  }

  refreshSettings(): Observable<ClientSetting[]> {
    return this.loadClientSettings();
  }

  isSalesDiscountEditEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const discountSetting = settings.find((s) => s.field === 'salesdiscount');
    return !!(
      discountSetting &&
      discountSetting.status === 1 &&
      discountSetting.value === 1
    );
  }

  isSalesPriceEditEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const priceSetting = settings.find((s) => s.field === 'salesprice');
    return !!(
      priceSetting &&
      priceSetting.status === 1 &&
      priceSetting.value === 1
    );
  }

  /**
   * When true, the unit price column shows selling price inclusive of tax.
   * When false (default), shows selling price exclusive of tax.
   */
  isUnitPriceInclTaxEnabled(): boolean {
    const settings = this.settingsSubject.value;
    const setting = settings.find((s) => s.field === 'enable_inclusive_tax');
    return !!(setting && setting.status === 1 && setting.value === 1);
  }
}
